import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  RefreshControl,
  Dimensions,
  TextInput,
  FlatList,
  Platform,
  Image,
  ScrollView,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import Icon from 'react-native-vector-icons/MaterialIcons';
import * as Location from 'expo-location';
import { listEvents, listUsersWithLocation } from '../services/database';
import { getProfilePictureUrl } from '../services/storage';
import { getColors, getShadows, typography, spacing, borderRadius } from '../styles/theme';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isMobile = SCREEN_WIDTH < 768;

// =====================
// Haversine distance (km)
// =====================
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// =====================
// Formatters
// =====================
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
};

const formatTime = (timeString) => {
  if (!timeString) return '';
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes}${ampm}`;
};

// =====================
// Sort options
// =====================
const SORT_OPTIONS = [
  { key: 'recommended', label: 'Recommended', icon: 'auto-awesome' },
  { key: 'nearest', label: 'Nearest', icon: 'near-me' },
  { key: 'popular', label: 'Popular', icon: 'trending-up' },
  { key: 'soonest', label: 'Soonest', icon: 'event' },
];

const PLAYER_SORT_OPTIONS = [
  { key: 'recommended', label: 'Recommended', icon: 'auto-awesome' },
  { key: 'nearest', label: 'Nearest', icon: 'near-me' },
  { key: 'recent', label: 'Recently Active', icon: 'access-time' },
];

const RADIUS_OPTIONS = [5, 10, 25, 50, 100];

// Quick Pair–style button look (match MapScreen Quick Pairing button)
const QUICK_PAIR_GREEN = '#84D288';
const QUICK_PAIR_SHADOW = {
  shadowColor: '#76AF87',
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 1,
  shadowRadius: 0,
  elevation: 8,
};
const QUICK_PAIR_RADIUS = 10;

// =====================
// Tab options
// =====================
const TABS = [
  { key: 'events', label: 'Events', icon: 'event' },
  { key: 'players', label: 'Players', icon: 'person' },
];

// =====================
// Event type display helpers
// =====================
const EVENT_TYPE_COLORS_LIGHT = {
  CASUAL: { bg: '#E8F5E9', text: '#2E7D32' },
  BLITZ: { bg: '#FFF3E0', text: '#E65100' },
  TOURNAMENT: { bg: '#E3F2FD', text: '#1565C0' },
  CLASS: { bg: '#F3E5F5', text: '#6A1B9A' },
};
const EVENT_TYPE_COLORS_DARK = {
  CASUAL: { bg: 'rgba(76,175,80,0.18)', text: '#81C784' },
  BLITZ: { bg: 'rgba(255,152,0,0.18)', text: '#FFB74D' },
  TOURNAMENT: { bg: 'rgba(33,150,243,0.18)', text: '#64B5F6' },
  CLASS: { bg: 'rgba(156,39,176,0.18)', text: '#CE93D8' },
};

// =====================
// Subtle checker pattern background
// =====================
const CheckerBackground = React.memo(({ isDarkMode }) => {
  // Render a grid of alternating-opacity squares
  const squareSize = 40;
  const cols = Math.ceil(SCREEN_WIDTH / squareSize) + 1;
  const rows = 6; // just enough to fill behind the header
  const baseOpacity = isDarkMode ? 0.04 : 0.03;

  const squares = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if ((r + c) % 2 === 0) {
        squares.push(
          <View
            key={`${r}-${c}`}
            style={{
              position: 'absolute',
              top: r * squareSize,
              left: c * squareSize,
              width: squareSize,
              height: squareSize,
              backgroundColor: isDarkMode ? '#FFFFFF' : '#000000',
              opacity: baseOpacity,
              borderRadius: 2,
            }}
          />,
        );
      }
    }
  }

  return (
    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }} pointerEvents="none">
      {squares}
    </View>
  );
});

// =====================
// EventCard component (theme-aware)
// =====================
const EventCard = React.memo(({ item, onPress, onClubPress, colors, shadowStyle, isDarkMode }) => {
  const rsvpCount = item.rsvpCount || 0;
  const eventLocation = item.location_address || item.location || 'Location TBD';
  const distance = item.distance != null && isFinite(item.distance)
    ? `${item.distance.toFixed(1)} km`
    : null;
  const typeColors = isDarkMode ? EVENT_TYPE_COLORS_DARK : EVENT_TYPE_COLORS_LIGHT;
  const typeStyle = typeColors[item.event_type] || { bg: colors.backgroundDark || colors.borderLight, text: colors.textSecondary };
  const clubName = item.club?.club_name || item.club?.username || 'Unknown Club';

  return (
    <TouchableOpacity
      style={[styles.eventCard, { backgroundColor: colors.surface, borderColor: colors.borderLight }, shadowStyle]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      {/* Top row: title + price badge */}
      <View style={styles.cardTopRow}>
        <Text style={[styles.cardTitle, { color: colors.text }]} numberOfLines={2}>{item.title}</Text>
        {item.is_paid ? (
          <View style={[styles.priceBadge, { backgroundColor: isDarkMode ? 'rgba(255,152,0,0.15)' : '#FFF3E0' }]}>
            <Text style={[styles.priceText, { color: isDarkMode ? '#FFB74D' : '#E65100' }]}>${item.price?.toFixed(0)}</Text>
          </View>
        ) : (
          <View style={[styles.freeBadge, { backgroundColor: isDarkMode ? 'rgba(76,175,80,0.15)' : '#E8F5E9' }]}>
            <Text style={[styles.freeText, { color: isDarkMode ? '#81C784' : '#2E7D32' }]}>Free</Text>
          </View>
        )}
      </View>

      {/* Club name */}
      <TouchableOpacity
        style={styles.clubRow}
        onPress={onClubPress}
        activeOpacity={0.6}
        hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
      >
        <Icon name="groups" size={15} color={colors.primary} />
        <Text style={[styles.clubName, { color: colors.primary }]} numberOfLines={1}>{clubName}</Text>
        <Icon name="chevron-right" size={16} color={colors.textLight} />
      </TouchableOpacity>

      {/* Details grid */}
      <View style={styles.detailsGrid}>
        <View style={styles.detailItem}>
          <Icon name="event" size={15} color={colors.textSecondary} />
          <Text style={[styles.detailText, { color: colors.textSecondary }]}>{formatDate(item.event_date)}</Text>
        </View>
        <View style={styles.detailItem}>
          <Icon name="schedule" size={15} color={colors.textSecondary} />
          <Text style={[styles.detailText, { color: colors.textSecondary }]}>
            {formatTime(item.event_time)}
            {item.end_time ? ` - ${formatTime(item.end_time)}` : ''}
          </Text>
        </View>
        <View style={styles.detailItem}>
          <Icon name="location-on" size={15} color={colors.textSecondary} />
          <Text style={[styles.detailText, { color: colors.textSecondary }]} numberOfLines={1}>{eventLocation}</Text>
        </View>
      </View>

      {/* Bottom row */}
      <View style={[styles.cardBottomRow, { borderTopColor: colors.borderLight }]}>
        <View style={styles.cardBottomLeft}>
          {item.event_type && (
            <View style={[styles.typeTag, { backgroundColor: typeStyle.bg }]}>
              <Text style={[styles.typeTagText, { color: typeStyle.text }]}>
                {item.event_type.charAt(0) + item.event_type.slice(1).toLowerCase()}
              </Text>
            </View>
          )}
          {distance && (
            <View style={[styles.distanceBadge, { backgroundColor: colors.background, borderColor: colors.borderLight }]}>
              <Icon name="straighten" size={12} color={colors.primary} />
              <Text style={[styles.distanceText, { color: colors.primary }]}>{distance}</Text>
            </View>
          )}
        </View>
        <View style={styles.cardBottomRight}>
          <View style={styles.rsvpBadge}>
            <Icon name="people" size={14} color={colors.primary} />
            <Text style={[styles.rsvpText, { color: colors.primary }]}>{rsvpCount} going</Text>
          </View>
          <Icon name="chevron-right" size={20} color={colors.textLight} />
        </View>
      </View>
    </TouchableOpacity>
  );
});

// =====================
// PlayerCard component (theme-aware)
// =====================
const PlayerCard = React.memo(({ item, onPress, colors, shadowStyle, isDarkMode }) => {
  const avatarUrl = getProfilePictureUrl(item.profile_picture_url, 'thumbnail');
  const distance = item.distance != null && isFinite(item.distance)
    ? `${item.distance.toFixed(1)} km`
    : null;

  const rating = item.chesscom_rapid || item.chesscom_blitz || item.lichess_rapid || item.lichess_blitz || item.rating;
  const ratingSource = item.chesscom_rapid ? 'Chess.com'
    : item.chesscom_blitz ? 'Chess.com'
    : item.lichess_rapid ? 'Lichess'
    : item.lichess_blitz ? 'Lichess'
    : item.rating ? 'In-app' : null;

  const lastSeen = (() => {
    if (item.is_online) return 'Online now';
    if (!item.last_online_at) return null;
    const diff = Date.now() - new Date(item.last_online_at).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return null;
  })();

  return (
    <TouchableOpacity
      style={[styles.playerCard, { backgroundColor: colors.surface, borderColor: colors.borderLight }, shadowStyle]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={styles.playerCardRow}>
        {/* Avatar */}
        {avatarUrl ? (
          <Image source={{ uri: avatarUrl }} style={[styles.playerAvatar, { borderColor: colors.primary + '40' }]} />
        ) : (
          <View style={[styles.playerAvatar, styles.playerAvatarPlaceholder, { backgroundColor: colors.background, borderColor: colors.borderLight }]}>
            <Icon name="person" size={28} color={colors.textLight} />
          </View>
        )}

        {/* Info */}
        <View style={styles.playerInfo}>
          <View style={styles.playerNameRow}>
            <Text style={[styles.playerName, { color: colors.text }]} numberOfLines={1}>{item.username || 'Player'}</Text>
            {item.is_online && <View style={styles.onlineDot} />}
          </View>

          {(item.city || item.status_message) && (
            <Text style={[styles.playerSubtext, { color: colors.textSecondary }]} numberOfLines={1}>
              {item.status_message || item.city}
            </Text>
          )}

          <View style={styles.playerDetailsRow}>
            {rating && ratingSource && (
              <View style={[styles.playerRatingBadge, { backgroundColor: colors.primary + '15', borderColor: colors.primary + '30' }]}>
                <Icon name="emoji-events" size={12} color={colors.primary} />
                <Text style={[styles.playerRatingText, { color: colors.primary }]}>{rating} {ratingSource}</Text>
              </View>
            )}
            {distance && (item.show_distance_in_discover === true) && (
              <View style={[styles.playerDistanceBadge, { backgroundColor: colors.background, borderColor: colors.borderLight }]}>
                <Icon name="straighten" size={12} color={colors.primary} />
                <Text style={[styles.playerDistanceText, { color: colors.primary }]}>{distance}</Text>
              </View>
            )}
            {lastSeen && (
              <Text style={[styles.playerLastSeen, { color: item.is_online ? '#4CAF50' : colors.textLight }]}>
                {lastSeen}
              </Text>
            )}
          </View>
        </View>

        <Icon name="chevron-right" size={22} color={colors.textLight} />
      </View>
    </TouchableOpacity>
  );
});

// =====================
// Main Screen
// =====================
const EventDiscoveryScreen = ({ navigation }) => {
  const { appUser } = useAuth();
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const themeShadows = getShadows(isDarkMode);

  // Tab state
  const [activeTab, setActiveTab] = useState('events');

  // Data state
  const [events, setEvents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  // Filter / sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [radiusKm, setRadiusKm] = useState(50);
  const [sortBy, setSortBy] = useState('recommended');
  const [playerSortBy, setPlayerSortBy] = useState('recommended');

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    getLocation();
    fetchEvents();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch players when tab switches to players (lazy load)
  useEffect(() => {
    if (activeTab === 'players' && players.length === 0 && !playersLoading) {
      fetchPlayers();
    }
  }, [activeTab]);

  // =====================
  // Location
  // =====================
  const getLocation = async () => {
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status === 'granted') {
        const loc = await Location.getCurrentPositionAsync({});
        if (isMountedRef.current) {
          setUserLocation({
            latitude: loc.coords.latitude,
            longitude: loc.coords.longitude,
          });
        }
      }
    } catch (error) {
      console.error('Error getting location:', error);
    }
  };

  // =====================
  // Fetch events from Supabase
  // =====================
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await listEvents({
        fromDate: new Date().toISOString(),
      });

      if (!isMountedRef.current) return;

      const normalized = (data || []).map((evt) => {
        let rsvpCount = 0;
        if (Array.isArray(evt.rsvps)) {
          if (evt.rsvps.length > 0 && evt.rsvps[0].count != null) {
            rsvpCount = evt.rsvps[0].count;
          } else {
            rsvpCount = evt.rsvps.length;
          }
        }

        let likesCount = 0;
        if (Array.isArray(evt.likes)) {
          if (evt.likes.length > 0 && evt.likes[0].count != null) {
            likesCount = evt.likes[0].count;
          } else {
            likesCount = evt.likes.length;
          }
        }

        return { ...evt, rsvpCount, likesCount };
      });

      setEvents(normalized);
    } catch (error) {
      console.error('Error fetching events:', error);
      if (isMountedRef.current) {
        showToast('Failed to load events', 'error');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  // =====================
  // Fetch players from Supabase
  // =====================
  const fetchPlayers = async () => {
    try {
      setPlayersLoading(true);
      const data = await listUsersWithLocation({ accountType: 'PLAYER', limit: 500 });

      if (!isMountedRef.current) return;

      const filtered = (data || []).filter(
        (u) => appUser?.id && u.id !== appUser.id,
      );

      setPlayers(filtered);
    } catch (error) {
      console.error('Error fetching players:', error);
      if (isMountedRef.current) {
        showToast('Failed to load players', 'error');
      }
    } finally {
      if (isMountedRef.current) {
        setPlayersLoading(false);
        setRefreshing(false);
      }
    }
  };

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    getLocation();
    if (activeTab === 'events') {
      fetchEvents();
    } else {
      fetchPlayers();
    }
  }, [activeTab]);

  // =====================
  // Derived: filtered + sorted events (memoized)
  // =====================
  const filteredEvents = useMemo(() => {
    let list = [...events];

    if (userLocation) {
      list = list.map((evt) => ({
        ...evt,
        distance: calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          evt.location_latitude || evt.club?.latitude,
          evt.location_longitude || evt.club?.longitude,
        ),
      }));
    }

    if (userLocation) {
      list = list.filter((evt) => evt.distance != null && evt.distance <= radiusKm);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (evt) =>
          evt.title?.toLowerCase().includes(q) ||
          evt.description?.toLowerCase().includes(q) ||
          evt.club?.club_name?.toLowerCase().includes(q) ||
          evt.club?.username?.toLowerCase().includes(q) ||
          evt.location_address?.toLowerCase().includes(q) ||
          evt.location?.toLowerCase().includes(q),
      );
    }

    if (sortBy === 'recommended') {
      const maxRsvps = Math.max(...list.map((e) => e.rsvpCount || 0), 1);
      list.sort((a, b) => {
        const distA = 1 - Math.min((a.distance || radiusKm) / radiusKm, 1);
        const distB = 1 - Math.min((b.distance || radiusKm) / radiusKm, 1);
        const popA = (a.rsvpCount || 0) / maxRsvps;
        const popB = (b.rsvpCount || 0) / maxRsvps;
        const scoreA = 0.6 * distA + 0.4 * popA;
        const scoreB = 0.6 * distB + 0.4 * popB;
        return scoreB - scoreA;
      });
    } else if (sortBy === 'nearest') {
      list.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
    } else if (sortBy === 'popular') {
      list.sort((a, b) => (b.rsvpCount || 0) - (a.rsvpCount || 0));
    } else if (sortBy === 'soonest') {
      list.sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    }

    return list;
  }, [events, userLocation, radiusKm, searchQuery, sortBy]);

  // =====================
  // Derived: filtered + sorted players (memoized)
  // =====================
  const filteredPlayers = useMemo(() => {
    let list = [...players];

    if (userLocation) {
      list = list.map((player) => ({
        ...player,
        distance: calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          player.latitude,
          player.longitude,
        ),
      }));
    }

    if (userLocation) {
      list = list.filter((p) => p.distance != null && p.distance <= radiusKm);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          p.username?.toLowerCase().includes(q) ||
          p.city?.toLowerCase().includes(q) ||
          p.status_message?.toLowerCase().includes(q) ||
          p.chesscom_username?.toLowerCase().includes(q) ||
          p.lichess_username?.toLowerCase().includes(q),
      );
    }

    if (playerSortBy === 'recommended') {
      const now = Date.now();
      const maxAge = 7 * 24 * 60 * 60 * 1000;
      list.sort((a, b) => {
        const distA = 1 - Math.min((a.distance || radiusKm) / radiusKm, 1);
        const distB = 1 - Math.min((b.distance || radiusKm) / radiusKm, 1);
        const ageA = a.last_online_at ? now - new Date(a.last_online_at).getTime() : maxAge;
        const ageB = b.last_online_at ? now - new Date(b.last_online_at).getTime() : maxAge;
        const recencyA = 1 - Math.min(ageA / maxAge, 1);
        const recencyB = 1 - Math.min(ageB / maxAge, 1);
        const scoreA = 0.7 * distA + 0.3 * recencyA;
        const scoreB = 0.7 * distB + 0.3 * recencyB;
        return scoreB - scoreA;
      });
    } else if (playerSortBy === 'nearest') {
      list.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
    } else if (playerSortBy === 'recent') {
      list.sort((a, b) => {
        const timeA = a.last_online_at ? new Date(a.last_online_at).getTime() : 0;
        const timeB = b.last_online_at ? new Date(b.last_online_at).getTime() : 0;
        return timeB - timeA;
      });
    }

    return list;
  }, [players, userLocation, radiusKm, searchQuery, playerSortBy]);

  // =====================
  // Handlers
  // =====================
  const handleEventPress = useCallback(
    (eventId) => {
      navigation.navigate('EventDetails', { eventId });
    },
    [navigation],
  );

  const handleClubPress = useCallback(
    (clubId) => {
      if (clubId) {
        navigation.navigate('UserDetail', { userId: clubId });
      }
    },
    [navigation],
  );

  const handlePlayerPress = useCallback(
    (playerId) => {
      navigation.navigate('UserDetail', { userId: playerId });
    },
    [navigation],
  );

  // =====================
  // Render helpers
  // =====================
  const cardShadow = themeShadows.medium;

  const renderEventCard = useCallback(
    ({ item }) => (
      <EventCard
        item={item}
        onPress={() => handleEventPress(item.id)}
        onClubPress={() => handleClubPress(item.club?.id)}
        colors={colors}
        shadowStyle={cardShadow}
        isDarkMode={isDarkMode}
      />
    ),
    [handleEventPress, handleClubPress, colors, cardShadow, isDarkMode],
  );

  const renderPlayerCard = useCallback(
    ({ item }) => (
      <PlayerCard
        item={item}
        onPress={() => handlePlayerPress(item.id)}
        colors={colors}
        shadowStyle={cardShadow}
        isDarkMode={isDarkMode}
      />
    ),
    [handlePlayerPress, colors, cardShadow, isDarkMode],
  );

  const keyExtractor = useCallback((item) => item.id, []);

  // =====================
  // Loading state
  // =====================
  const isLoading = activeTab === 'events' ? loading : playersLoading;
  if (isLoading && ((activeTab === 'events' && events.length === 0) || (activeTab === 'players' && players.length === 0))) {
    return (
      <View style={[styles.centered, { backgroundColor: colors.background }]}>
        <LoadingAnimation size={525} />
        <Text style={[styles.loadingText, { color: colors.textSecondary }]}>
          {activeTab === 'events' ? 'Loading events...' : 'Loading players...'}
        </Text>
      </View>
    );
  }

  // =====================
  // Active sort options depend on tab
  // =====================
  const activeSortOptions = activeTab === 'events' ? SORT_OPTIONS : PLAYER_SORT_OPTIONS;
  const activeSortBy = activeTab === 'events' ? sortBy : playerSortBy;
  const setActiveSortBy = activeTab === 'events' ? setSortBy : setPlayerSortBy;

  // =====================
  // Main render
  // =====================
  const windowHeight = Dimensions.get('window').height;
  return (
    <View style={[
      styles.container,
      { backgroundColor: colors.background },
      Platform.OS === 'web' && { height: windowHeight, maxHeight: windowHeight },
    ]}>

      {/* ---- Header with checker background ---- */}
      <View style={[styles.header, isMobile && styles.headerMobile, { backgroundColor: colors.surface, borderBottomColor: colors.borderLight }, themeShadows.small]}>
        <CheckerBackground isDarkMode={isDarkMode} />

        <View style={[styles.headerTopRow, isMobile && styles.headerTopRowMobile]}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={[styles.backButton, isMobile && styles.backButtonMobile, { backgroundColor: colors.background + (isDarkMode ? 'AA' : '99') }]}
          >
            <Icon name="arrow-back" size={isMobile ? 20 : 24} color={colors.text} />
          </TouchableOpacity>
          <View style={styles.headerTitleWrap}>
            <Text style={[styles.headerTitle, isMobile && styles.headerTitleMobile, { color: colors.text }]}>Discover</Text>
            <Text style={[styles.headerSubtitle, isMobile && styles.headerSubtitleMobile, { color: colors.textSecondary }]}>
              {activeTab === 'events' ? 'chess events near you' : 'chess players near you'}
            </Text>
          </View>
          <View style={{ width: isMobile ? 38 : 44 }} />
        </View>

        {/* Tab selector */}
        <View style={[styles.tabRow, isMobile && styles.tabRowMobile, { backgroundColor: colors.background, borderColor: colors.borderLight }]}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <TouchableOpacity
                key={tab.key}
                style={[
                  styles.tabButton,
                  isMobile && styles.tabButtonMobile,
                  { borderRadius: QUICK_PAIR_RADIUS },
                  isActive && [
                    styles.tabButtonActive,
                    { backgroundColor: QUICK_PAIR_GREEN, borderWidth: 0 },
                    QUICK_PAIR_SHADOW,
                  ],
                ]}
                onPress={() => setActiveTab(tab.key)}
                activeOpacity={0.8}
              >
                <Icon
                  name={tab.icon}
                  size={isMobile ? 16 : 18}
                  color={isActive ? '#fff' : colors.textSecondary}
                />
                <Text style={[
                  styles.tabButtonText,
                  isMobile && styles.tabButtonTextMobile,
                  { color: colors.textSecondary },
                  isActive && styles.tabButtonTextActive,
                ]}>
                  {tab.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Search bar */}
        <View style={[styles.searchContainer, isMobile && styles.searchContainerMobile, { backgroundColor: colors.background, borderColor: colors.borderLight }]}>
          <Icon name="search" size={isMobile ? 16 : 20} color={colors.textLight} />
          <TextInput
            style={[styles.searchInput, isMobile && styles.searchInputMobile, { color: colors.text }]}
            placeholder={activeTab === 'events' ? 'Search events, clubs, locations...' : 'Search players, cities...'}
            placeholderTextColor={colors.textLight}
            value={searchQuery}
            onChangeText={setSearchQuery}
            returnKeyType="search"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Icon name="close" size={isMobile ? 16 : 18} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>

        {/* Radius chips */}
        <View style={[styles.chipRow, isMobile && styles.chipRowMobile]}>
          {isMobile ? (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.chipRowScrollMobile}
            >
              <Icon name="my-location" size={14} color={QUICK_PAIR_GREEN} style={{ marginRight: 2 }} />
              <Text style={[styles.chipLabel, styles.chipLabelMobile, { color: colors.textSecondary, marginRight: 6 }]}>Within</Text>
              {RADIUS_OPTIONS.map((r) => {
                const isActive = radiusKm === r;
                return (
                  <TouchableOpacity
                    key={r}
                    style={[
                      styles.chip,
                      styles.chipMobile,
                      { marginRight: 6, borderRadius: QUICK_PAIR_RADIUS, backgroundColor: colors.background, borderColor: colors.borderLight },
                      isActive && [
                        { backgroundColor: QUICK_PAIR_GREEN, borderColor: QUICK_PAIR_GREEN },
                        QUICK_PAIR_SHADOW,
                      ],
                    ]}
                    onPress={() => setRadiusKm(r)}
                    activeOpacity={0.8}
                  >
                    <Text style={[
                      styles.chipText,
                      styles.chipTextMobile,
                      { color: colors.textSecondary },
                      isActive && { color: '#fff', fontWeight: typography.bold },
                    ]}>
                      {r} km
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          ) : (
            <>
              <Icon name="my-location" size={16} color={QUICK_PAIR_GREEN} style={{ marginRight: 4 }} />
              <Text style={[styles.chipLabel, { color: colors.textSecondary }]}>Within</Text>
              {RADIUS_OPTIONS.map((r) => {
                const isActive = radiusKm === r;
                return (
                  <TouchableOpacity
                    key={r}
                    style={[
                      styles.chip,
                      { borderRadius: QUICK_PAIR_RADIUS, backgroundColor: colors.background, borderColor: colors.borderLight },
                      isActive && [
                        { backgroundColor: QUICK_PAIR_GREEN, borderColor: QUICK_PAIR_GREEN },
                        QUICK_PAIR_SHADOW,
                      ],
                    ]}
                    onPress={() => setRadiusKm(r)}
                    activeOpacity={0.8}
                  >
                    <Text style={[
                      styles.chipText,
                      { color: colors.textSecondary },
                      isActive && { color: '#fff', fontWeight: typography.bold },
                    ]}>
                      {r} km
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </>
          )}
        </View>

        {/* Sort pills */}
        <View style={[styles.chipRow, isMobile && styles.sortRowMobile]}>
          {isMobile ? (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.sortRowScrollMobile}
            >
              {activeSortOptions.map((opt) => {
                const isActive = activeSortBy === opt.key;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    style={[
                      styles.sortPill,
                      styles.sortPillMobile,
                      { marginRight: 6, borderRadius: QUICK_PAIR_RADIUS, backgroundColor: colors.background, borderColor: colors.borderLight },
                      isActive && [
                        { backgroundColor: QUICK_PAIR_GREEN, borderColor: QUICK_PAIR_GREEN },
                        QUICK_PAIR_SHADOW,
                      ],
                    ]}
                    onPress={() => setActiveSortBy(opt.key)}
                    activeOpacity={0.8}
                  >
                    <Icon
                      name={opt.icon}
                      size={13}
                      color={isActive ? '#fff' : colors.textSecondary}
                    />
                    <Text style={[
                      styles.sortPillText,
                      styles.sortPillTextMobile,
                      { color: colors.textSecondary },
                      isActive && { color: '#fff', fontWeight: typography.bold },
                    ]}>
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          ) : (
            <>
              {activeSortOptions.map((opt) => {
                const isActive = activeSortBy === opt.key;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    style={[
                      styles.sortPill,
                      { borderRadius: QUICK_PAIR_RADIUS, backgroundColor: colors.background, borderColor: colors.borderLight },
                      isActive && [
                        { backgroundColor: QUICK_PAIR_GREEN, borderColor: QUICK_PAIR_GREEN },
                        QUICK_PAIR_SHADOW,
                      ],
                    ]}
                    onPress={() => setActiveSortBy(opt.key)}
                    activeOpacity={0.8}
                  >
                    <Icon
                      name={opt.icon}
                      size={15}
                      color={isActive ? '#fff' : colors.textSecondary}
                    />
                    <Text style={[
                      styles.sortPillText,
                      { color: colors.textSecondary },
                      isActive && { color: '#fff', fontWeight: typography.bold },
                    ]}>
                      {opt.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </>
          )}
        </View>

        {/* Results count */}
        <Text style={[styles.resultsCount, isMobile && styles.resultsCountMobile, { color: colors.textLight }]}>
          {activeTab === 'events'
            ? `${filteredEvents.length} event${filteredEvents.length !== 1 ? 's' : ''} found`
            : `${filteredPlayers.length} player${filteredPlayers.length !== 1 ? 's' : ''} found`}
        </Text>
      </View>

      {/* ---- Events List ---- */}
      {activeTab === 'events' && (
        <View style={styles.listWrapper}>
        <FlatList
          style={styles.list}
          data={filteredEvents}
          renderItem={renderEventCard}
          keyExtractor={keyExtractor}
          contentContainerStyle={[styles.listContent, isMobile && styles.listContentMobile]}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <View style={[styles.emptyIconWrap, { backgroundColor: colors.primary + '15' }]}>
                <Icon name="event-busy" size={48} color={colors.primary} />
              </View>
              <Text style={[styles.emptyText, { color: colors.text }]}>
                {events.length === 0 ? 'No upcoming events' : 'No events match your filters'}
              </Text>
              <Text style={[styles.emptySubtext, { color: colors.textSecondary }]}>
                {events.length === 0
                  ? 'Check back later for new events!'
                  : 'Try adjusting your search or radius'}
              </Text>
            </View>
          }
        />
        </View>
      )}

      {/* ---- Players List ---- */}
      {activeTab === 'players' && (
        <View style={styles.listWrapper}>
        <FlatList
          style={styles.list}
          data={filteredPlayers}
          renderItem={renderPlayerCard}
          keyExtractor={keyExtractor}
          contentContainerStyle={[styles.listContent, isMobile && styles.listContentMobile]}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <View style={[styles.emptyIconWrap, { backgroundColor: colors.primary + '15' }]}>
                <Icon name="person-off" size={48} color={colors.primary} />
              </View>
              <Text style={[styles.emptyText, { color: colors.text }]}>
                {players.length === 0 ? 'No players in this area' : 'No players match your filters'}
              </Text>
              <Text style={[styles.emptySubtext, { color: colors.textSecondary }]}>
                {players.length === 0
                  ? 'Try increasing the radius or check back later!'
                  : 'Try adjusting your search or radius'}
              </Text>
            </View>
          }
        />
        </View>
      )}
    </View>
  );
};

// =====================
// Static styles (theme-independent layout)
// =====================
const styles = StyleSheet.create({
  // Screen
  container: {
    flex: 1,
    minHeight: 0,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.body,
  },

  // Header
  header: {
    paddingTop: Platform.OS === 'ios' ? 54 : 28,
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    overflow: 'hidden', // clip checker pattern
  },
  headerMobile: {
    paddingTop: Platform.OS === 'ios' ? 36 : 16,
    paddingHorizontal: 10,
    paddingBottom: 4,
  },
  headerTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  headerTopRowMobile: {
    marginBottom: 4,
  },
  backButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backButtonMobile: {
    width: 38,
    height: 38,
    borderRadius: 19,
  },
  headerTitleWrap: {
    alignItems: 'center',
    flex: 1,
  },
  headerTitle: {
    fontSize: typography.h3,
    fontWeight: typography.bold,
    letterSpacing: -0.5,
  },
  headerTitleMobile: {
    fontSize: 18,
    fontWeight: typography.bold,
  },
  headerSubtitle: {
    fontSize: typography.caption,
    marginTop: 2,
    fontStyle: 'italic',
  },
  headerSubtitleMobile: {
    fontSize: 11,
    marginTop: 0,
  },

  // Tab row
  tabRow: {
    flexDirection: 'row',
    marginBottom: spacing.md,
    borderRadius: borderRadius.lg,
    padding: 4,
    borderWidth: 1,
    gap: 4,
  },
  tabRowMobile: {
    marginBottom: 5,
    padding: 3,
  },
  tabButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: borderRadius.md,
  },
  tabButtonMobile: {
    paddingVertical: 6,
    gap: 4,
  },
  tabButtonActive: {
    // backgroundColor set dynamically
  },
  tabButtonText: {
    fontSize: typography.body,
    fontWeight: typography.semiBold,
  },
  tabButtonTextMobile: {
    fontSize: 12,
  },
  tabButtonTextActive: {
    color: '#fff',
  },

  // Search
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: borderRadius.xl,
    paddingHorizontal: spacing.sm + 4,
    paddingVertical: Platform.OS === 'ios' ? 11 : 7,
    marginBottom: spacing.sm,
    borderWidth: 1,
    gap: spacing.sm,
  },
  searchContainerMobile: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginBottom: 5,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.bodySmall,
    padding: 0,
  },
  searchInputMobile: {
    fontSize: 14,
  },

  // Chip row (radius + sort)
  chipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
    flexWrap: 'wrap',
    gap: 12,
  },
  chipRowMobile: {
    marginBottom: 4,
    gap: 6,
  },
  chipRowScrollMobile: {
    marginBottom: 4,
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 12,
  },
  chipLabel: {
    fontSize: typography.bodySmall,
    marginRight: 4,
  },
  chipLabelMobile: {
    fontSize: 10,
    marginRight: 2,
  },
  chip: {
    paddingHorizontal: spacing.md,
    paddingVertical: 8,
    borderRadius: borderRadius.pill,
    borderWidth: 1,
  },
  chipMobile: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  chipText: {
    fontSize: typography.bodySmall,
    fontWeight: typography.medium,
  },
  chipTextMobile: {
    fontSize: 10,
  },

  // Sort pills
  sortPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: borderRadius.pill,
    borderWidth: 1,
  },
  sortPillMobile: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 3,
  },
  sortPillText: {
    fontSize: typography.bodySmall,
    fontWeight: typography.medium,
  },
  sortPillTextMobile: {
    fontSize: 10,
  },
  sortRowMobile: {
    marginBottom: 4,
    gap: 6,
  },
  sortRowScrollMobile: {
    marginBottom: 2,
    flexDirection: 'row',
    paddingRight: 12,
  },

  // Results count
  resultsCount: {
    fontSize: typography.caption,
    textAlign: 'center',
    marginBottom: 2,
  },
  resultsCountMobile: {
    marginBottom: 0,
    fontSize: 11,
  },

  // List
  listWrapper: {
    flex: 1,
    minHeight: 0,
  },
  list: {
    flex: 1,
    minHeight: 0,
  },
  listContent: {
    padding: spacing.md,
    paddingBottom: 40,
  },
  listContentMobile: {
    paddingHorizontal: 10,
    paddingTop: 8,
    paddingBottom: 32,
  },

  // Event card
  eventCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
  },
  cardTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  cardTitle: {
    fontSize: typography.h6,
    fontWeight: typography.bold,
    flex: 1,
    marginRight: spacing.sm,
  },
  priceBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: borderRadius.pill,
  },
  priceText: {
    fontSize: typography.caption,
    fontWeight: typography.bold,
  },
  freeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: borderRadius.pill,
  },
  freeText: {
    fontSize: typography.caption,
    fontWeight: typography.bold,
  },

  // Club row
  clubRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
  },
  clubName: {
    fontSize: typography.bodySmall,
    fontWeight: typography.medium,
    flex: 1,
  },

  // Details grid
  detailsGrid: {
    gap: 5,
    marginBottom: 10,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  detailText: {
    fontSize: typography.bodySmall,
    flex: 1,
  },

  // Bottom row
  cardBottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
    borderTopWidth: 1,
  },
  cardBottomLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  cardBottomRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  typeTag: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: borderRadius.pill,
  },
  typeTagText: {
    fontSize: 11,
    fontWeight: typography.semiBold,
  },
  distanceBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: borderRadius.pill,
    borderWidth: 1,
  },
  distanceText: {
    fontSize: 11,
    fontWeight: typography.medium,
  },
  rsvpBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  rsvpText: {
    fontSize: typography.caption,
    fontWeight: typography.medium,
  },

  // Player card
  playerCard: {
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
  },
  playerCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  playerAvatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    marginRight: spacing.sm,
    borderWidth: 2,
  },
  playerAvatarPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  playerInfo: {
    flex: 1,
  },
  playerNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
  },
  playerName: {
    fontSize: typography.h6,
    fontWeight: typography.bold,
    flexShrink: 1,
  },
  onlineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#4CAF50',
  },
  playerSubtext: {
    fontSize: typography.caption,
    marginBottom: 4,
  },
  playerDetailsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  playerRatingBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: borderRadius.pill,
    borderWidth: 1,
  },
  playerRatingText: {
    fontSize: 11,
    fontWeight: typography.medium,
  },
  playerDistanceBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: borderRadius.pill,
    borderWidth: 1,
  },
  playerDistanceText: {
    fontSize: 11,
    fontWeight: typography.medium,
  },
  playerLastSeen: {
    fontSize: 11,
    fontWeight: typography.medium,
  },

  // Empty state
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xl * 3,
  },
  emptyIconWrap: {
    width: 88,
    height: 88,
    borderRadius: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  emptyText: {
    fontSize: typography.h5,
    fontWeight: typography.semiBold,
    marginTop: spacing.xs,
  },
  emptySubtext: {
    fontSize: typography.bodySmall,
    marginTop: spacing.sm,
    textAlign: 'center',
    paddingHorizontal: spacing.xl,
  },
});

export default EventDiscoveryScreen;
